import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";

// Extract session_id from either hash fragment (`#session_id=...`) or query string (`?session_id=...`)
const extractSessionId = () => {
  try {
    const hash = window.location.hash || "";
    const hashMatch = hash.match(/session_id=([^&]+)/);
    if (hashMatch) return decodeURIComponent(hashMatch[1]);
    const params = new URLSearchParams(window.location.search || "");
    const q = params.get("session_id");
    if (q) return q;
  } catch { /* fall through */ }
  return null;
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, checkAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const finish = async () => {
      // Wipe the session_id from the URL immediately so a page refresh doesn't retry a spent token
      try { window.history.replaceState(null, "", "/studio"); } catch {}
      // Definitive source of truth: /auth/me. If cookie is set, this hydrates the user.
      try { await checkAuth(); } catch { /* still navigate; overlay will re-prompt if no session */ }
      navigate("/studio", { replace: true });
    };

    const sessionId = extractSessionId();
    if (!sessionId) {
      // No session_id at all — probably a cached redirect. Just check /auth/me and continue.
      finish();
      return;
    }

    // Belt-and-braces timeout: if the session exchange hangs, force navigate after 8s
    const timeoutId = setTimeout(() => {
      if (!processed.done) { processed.done = true; finish(); }
    }, 8000);

    (async () => {
      try {
        const referralCode = localStorage.getItem("geneo_ref") || undefined;
        const { data } = await api.post("/auth/session", { session_id: sessionId, referral_code: referralCode });
        if (referralCode) localStorage.removeItem("geneo_ref");
        // Optimistic hydrate so the next render already sees the user
        if (data) setUser(data);
      } catch (e) {
        // Session exchange failed — checkAuth() below will still catch any pre-existing cookie
      } finally {
        clearTimeout(timeoutId);
        if (!processed.done) { processed.done = true; await finish(); }
      }
    })();
  }, [location, navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] glyph-bg">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#F2C94C] to-[#E27D60] flex items-center justify-center glow-gold floaty">
          <Sparkles className="w-8 h-8 text-[#0A0A0A]" />
        </div>
        <div className="font-mono-hy text-[11px] text-[#F2C94C] mt-6" data-testid="auth-callback-loader">Aligning the constellation…</div>
      </div>
    </div>
  );
}
